import { useEffect, useState } from 'react';
import SignUpForm from '../components/auth/SignUpForm';
import ConfirmEmail from '../components/auth/ConfirmEmail';
import ProfileTypeStep from '../components/auth/ProfileTypeStep';
import { useAuth } from '../context/auth';
import CompleteProfileForm from '../components/auth/CompleteProfileForm';
import ImproveProfileStep from '../components/auth/ImproveProfileStep';
import DemoWalkthroughStep from '../components/auth/DemoWalkthroughStep';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProfileStore } from '../stores/profileStore';
import { useUIStore } from '../stores/uiStore';
import type { ProfileType } from '../types/profile';
import { useShadowRecruiterStore } from '../stores/shadowRecruiterStore';
import { ROLES } from '../types/auth';
import type { ShadowRecruiterInviteContext } from '../types/job';

const SHADOW_INVITE_STORAGE_KEY = 'shadow_recruiter_invite_token';
const INVALID_SHADOW_INVITE_MESSAGE =
  'This invite link is invalid or has expired. Please request a new invite or sign up normally.';
const CLAIM_SHADOW_INVITE_MESSAGE =
  'This invite could not be claimed for your account. Please request a new invite or sign up normally.';

const FORCE_STEP_VALUES = [
  'confirm',
  'profileType',
  'completeProfile',
  'improveProfile',
  'demoWalkthrough',
] as const;
type ForceStepValue = (typeof FORCE_STEP_VALUES)[number];

function parseSignupParams(searchParams: URLSearchParams): {
  debug: boolean;
  forceStep: ForceStepValue | null;
  shadowInviteToken: string | null;
} {
  const debug = searchParams.get('debug') === 'true';
  const forceStepParam = searchParams.get('force_step');
  const shadowInviteToken = searchParams.get('shadow_invite');
  const forceStep =
    forceStepParam && FORCE_STEP_VALUES.includes(forceStepParam as ForceStepValue)
      ? (forceStepParam as ForceStepValue)
      : null;
  return { debug, forceStep, shadowInviteToken };
}

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setHideNavigationElements } = useUIStore();

  const {
    debug,
    forceStep,
    shadowInviteToken: inviteTokenFromUrl,
  } = parseSignupParams(searchParams);

  const [step, setStep] = useState<
    'form' | 'confirm' | 'profileType' | 'completeProfile' | 'improveProfile' | 'demoWalkthrough'
  >(debug && forceStep ? forceStep : 'form');
  const [email, setEmail] = useState('');
  const { user, resendConfirmationEmail } = useAuth();
  const { profile, fetchProfile } = useProfileStore();
  const { getInviteContext, claimInvite } = useShadowRecruiterStore();
  const [isFirstSignup, setIsFirstSignup] = useState(false);
  const [selectedProfileType, setSelectedProfileType] = useState<ProfileType | undefined>();
  const [shadowInviteToken, setShadowInviteToken] = useState<string | null>(inviteTokenFromUrl);
  const [shadowInviteLoading, setShadowInviteLoading] = useState(false);
  const [shadowInviteClaiming, setShadowInviteClaiming] = useState(false);
  const [shadowInviteContext, setShadowInviteContext] =
    useState<ShadowRecruiterInviteContext | null>(null);
  const [shadowInviteError, setShadowInviteError] = useState<string | null>(null);

  const clearShadowInvite = () => {
    window.localStorage.removeItem(SHADOW_INVITE_STORAGE_KEY);
    setShadowInviteToken(null);
    setShadowInviteContext(null);
  };

  useEffect(() => {
    setHideNavigationElements(true);
    return () => {
      setHideNavigationElements(false);
    };
  }, [setHideNavigationElements]);

  useEffect(() => {
    if (inviteTokenFromUrl) {
      setShadowInviteError(null);
      setShadowInviteToken(inviteTokenFromUrl);
      window.localStorage.setItem(SHADOW_INVITE_STORAGE_KEY, inviteTokenFromUrl);
      return;
    }

    const storedInviteToken = window.localStorage.getItem(SHADOW_INVITE_STORAGE_KEY);
    if (storedInviteToken) {
      setShadowInviteError(null);
      setShadowInviteToken(storedInviteToken);
    }
  }, [inviteTokenFromUrl]);

  useEffect(() => {
    if (!shadowInviteToken) {
      setShadowInviteContext(null);
      return;
    }

    let cancelled = false;

    const loadInviteContext = async () => {
      setShadowInviteLoading(true);
      try {
        const context = await getInviteContext(shadowInviteToken);
        if (!context) {
          if (!cancelled) {
            setShadowInviteError(INVALID_SHADOW_INVITE_MESSAGE);
            clearShadowInvite();
          }
          return;
        }

        if (!cancelled) {
          setShadowInviteContext(context);
          setShadowInviteError(null);
        }
      } catch {
        if (!cancelled) {
          setShadowInviteError(INVALID_SHADOW_INVITE_MESSAGE);
          clearShadowInvite();
        }
      } finally {
        if (!cancelled) {
          setShadowInviteLoading(false);
        }
      }
    };

    void loadInviteContext();

    return () => {
      cancelled = true;
    };
  }, [getInviteContext, shadowInviteToken]);

  useEffect(() => {
    if (
      !shadowInviteToken ||
      !shadowInviteContext ||
      shadowInviteClaiming ||
      !user?.id ||
      !user.email_confirmed_at
    ) {
      return;
    }

    if (profile?.role && profile.role !== ROLES.RECRUITER) {
      return;
    }

    let cancelled = false;

    const claimShadowInvite = async () => {
      setShadowInviteClaiming(true);
      try {
        await claimInvite(shadowInviteToken);
        await fetchProfile(user.id, { force: true });
        if (!cancelled) {
          setShadowInviteError(null);
          setSelectedProfileType(ROLES.RECRUITER);
          setIsFirstSignup(true);
          setStep('completeProfile');
          clearShadowInvite();
        }
      } catch {
        if (!cancelled) {
          setShadowInviteError(CLAIM_SHADOW_INVITE_MESSAGE);
          clearShadowInvite();
        }
      } finally {
        if (!cancelled) {
          setShadowInviteClaiming(false);
        }
      }
    };

    void claimShadowInvite();

    return () => {
      cancelled = true;
    };
  }, [
    claimInvite,
    fetchProfile,
    profile?.role,
    shadowInviteClaiming,
    shadowInviteContext,
    shadowInviteToken,
    user,
  ]);

  useEffect(() => {
    if (debug) {
      console.log(
        `debug: ${debug}, forceStep: ${forceStep}, user: ${user}, profile: ${profile}, step: ${step}, isFirstSignup: ${isFirstSignup}`
      );
    }

    if (debug && forceStep) {
      setStep(forceStep);
      return;
    }

    if (user) {
      if (shadowInviteToken && (shadowInviteLoading || shadowInviteClaiming)) {
        return;
      }

      if (!user.email_confirmed_at) {
        setEmail(user.email || '');
        setStep('confirm');
      } else if (shadowInviteContext) {
        setSelectedProfileType(ROLES.RECRUITER);
        if (!profile?.last_name) {
          setStep('completeProfile');
          setIsFirstSignup(true);
          return;
        }
        if (step !== 'improveProfile' && step !== 'demoWalkthrough') {
          navigate('/dashboard', { replace: true });
        }
      } else if (!profile?.role) {
        setStep('profileType');
      } else if (!profile?.last_name) {
        setStep('completeProfile');
        setIsFirstSignup(true);
      } else if (isFirstSignup && step === 'completeProfile') {
        setStep('improveProfile');
      } else if (step !== 'improveProfile' && step !== 'demoWalkthrough') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [
    debug,
    forceStep,
    isFirstSignup,
    navigate,
    profile,
    shadowInviteClaiming,
    shadowInviteContext,
    shadowInviteLoading,
    shadowInviteToken,
    step,
    user,
  ]);

  const handleSignUp = async ({
    isConfirmed,
    email,
  }: {
    isConfirmed: boolean;
    email: string | undefined;
  }) => {
    if (!isConfirmed) {
      setEmail(email || '');
      setStep('confirm');
    } else if (shadowInviteContext) {
      setSelectedProfileType(ROLES.RECRUITER);
      setStep('completeProfile');
    } else {
      setStep('profileType');
    }
  };

  const handleResend = async () => {
    if (!email) return;
    await resendConfirmationEmail();
  };

  const handleSelectProfileType = (type: ProfileType) => {
    setSelectedProfileType(type);
    setStep('completeProfile');
  };

  const handleCompleteProfile = () => {
    setStep('improveProfile');
    setIsFirstSignup(true);
  };

  const handleImproveProfileComplete = () => {
    setStep('demoWalkthrough');
  };

  const shadowInviteRedirectUrl =
    shadowInviteToken && typeof window !== 'undefined'
      ? `${window.location.origin}/signup?shadow_invite=${shadowInviteToken}`
      : undefined;

  return (
    <>
      {shadowInviteError ? (
        <div className="mx-auto mt-6 w-full max-w-3xl rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {shadowInviteError}
        </div>
      ) : null}
      {step === 'form' && (
        <SignUpForm
          onComplete={handleSignUp}
          initialFirstName={shadowInviteContext?.first_name || ''}
          initialEmail={shadowInviteContext?.email || ''}
          lockEmail={Boolean(shadowInviteContext?.email)}
          emailRedirectTo={shadowInviteRedirectUrl}
        />
      )}
      {step === 'confirm' && <ConfirmEmail email={email} onResend={handleResend} />}
      {step === 'profileType' && <ProfileTypeStep onSelect={handleSelectProfileType} />}
      {step === 'completeProfile' && (
        <CompleteProfileForm
          initialProfileType={selectedProfileType}
          onComplete={handleCompleteProfile}
          onBack={() => setStep('profileType')}
        />
      )}
      {step === 'improveProfile' && (
        <ImproveProfileStep onComplete={handleImproveProfileComplete} />
      )}
      {step === 'demoWalkthrough' && <DemoWalkthroughStep />}
    </>
  );
}
